<script setup lang="ts">
import {
  connectToActiveRoom,
  first_connected,
} from "../../actions/connectToActiveRoom";
import { activeRoomState } from "../../states/active_room";
import StartArea from "./newAreas/StartArea.vue";
import TilingArea from "./newAreas/TilingArea.vue";
import ScreenShareArea from "./newAreas/ScreenShareArea.vue";
import OneArea from "./newAreas/OneArea.vue";
import { nextTick, onMounted, ref, watch } from "vue";
import { useActiveRoomState } from "../../states/useActiveRoomState";
import LoadingConnect from "./newAreas/LoadingConnect.vue";
import SpeakerMode from "./newAreas/SpeakerMode.vue";
import {
  localScreenTrack,
} from "../../states/screenShareState";
import { usersState, selectedOneUserViewId } from "../../states/usersState";
import { loadingChatState } from "../../states/chatState";
import RightBlockWrapper from "../../components/rightBlock/RightBlockWrapper.vue";
import { viewState } from "../../states/viewState";
import { transitionEvents } from "../../states/transitions";
import RoomBarNew from "../../components/room/RoomBarNew.vue";
import ShareSpeakerNew from "../../components/ShareSpeakerNew.vue";
import { fullScreenModeState } from "../../states/fullScreenMode";
import { _showBar, getIsScreenShare, getShowBar } from "../../states/ui";

useActiveRoomState();
transitionEvents.emit("active");
onMounted(async () => {
  await connectToActiveRoom();
});
// Анимируем блок
const showRightBlock = ref(false);
// Удаляем из DOM блок после анимации
const vifRightBlock = ref(false);

watch(
  () => viewState.rightBlockContent,
  async (next, prev) => {
    if (next !== "none" && prev === "none") {
      vifRightBlock.value = true;
      await nextTick();
      requestAnimationFrame(() => {
        showRightBlock.value = true;
      });
    } else if (next === "none" && prev !== "none") {
      showRightBlock.value = false;
      setTimeout(() => {
        const el = document.querySelector(
          ".active-room-container .right-block.animated",
        );
        if (el) {
          Promise.all(el.getAnimations().map((a) => a.finished)).then(() => {
            vifRightBlock.value = false;
          });
        } else {
          vifRightBlock.value = false;
        }
      }, 100);
    }
  },
);
const isScreenShare = getIsScreenShare();
// Автоскрытие нижней панельки
const leftBlockEl = ref<HTMLElement | null>(null);
let timeout: NodeJS.Timeout | null = null;
const barListener = (ev: MouseEvent) => {
  const target = ev.currentTarget as HTMLElement;
  if (ev.clientY >= target.clientHeight - 76) {
    _showBar.value = true;
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  } else if (ev.clientY < target.clientHeight - 76) {
    if (timeout) {
      return;
    }
    timeout = setTimeout(() => {
      _showBar.value = false;
      timeout = null;
    }, 1000);
  }
};
watch(
  () => fullScreenModeState.value && isScreenShare.value,
  (next, prev) => {
    if (next && !prev) {
      _showBar.value = onBar.value;
      leftBlockEl.value?.addEventListener("mousemove", barListener);
    } else if (!next && prev) {
      _showBar.value = true;
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      leftBlockEl.value?.removeEventListener("mousemove", barListener);
    }
  },
);
const showBar = getShowBar();

const barEnter = () => {
  onBar.value = true;
};

const barLeave = () => {
  onBar.value = false;
};
const onBar = ref(false);
</script>
<template>
  <div class="active-room-container">
    <div class="speaker-container">
      <ShareSpeakerNew
        v-if="
          (activeRoomState.mode === 'stream' || localScreenTrack) &&
          viewState.showSpeakerInDemo
        "
        :has-move="viewState.rightBlockContent === 'none' ? true : false"
      />
      <div class="left-block">
        <div class="main-content"></div>
        <div
          class="bottom-block-spacer"
          :class="{
            opened:
              showBar,
          }"
        ></div>
        <div
          class="bottom-block"
          :class="{
            opened:
              showBar,
          }"
          @mouseenter="barEnter"
          @mouseleave="barLeave"
        >
          <RoomBarNew />
        </div>
      </div>
      <div
        class="right-block-spacer"
        :class="{
          opened: showRightBlock,
        }"
      ></div>
      <div
        v-if="vifRightBlock"
        class="right-block animated"
        :class="{
          opened: showRightBlock,
        }"
      >
        <div
          class="spacer"
          :class="{
            opened:
              (activeRoomState.mode === 'stream' || localScreenTrack) &&
              viewState.showSpeakerInDemo &&
              !viewState.fullScreenChat &&
              !viewState.pipMode,
          }"
        ></div>
        <RightBlockWrapper />
      </div>
    </div>
    <div ref="leftBlockEl" class="left-block">
      <div class="main-content">
        <LoadingConnect
          v-if="!first_connected || loadingChatState.startMessages"
        />
        <SpeakerMode v-else-if="activeRoomState.mode === 'speaker'" />
        <StartArea v-else-if="usersState.users.length < 2" />
        <TilingArea v-else-if="activeRoomState.mode === 'grid'" />
        <OneArea
          v-else-if="activeRoomState.mode === 'one' && selectedOneUserViewId"
        />
        <ScreenShareArea v-else-if="isScreenShare" />
      </div>
      <div
        class="bottom-block-spacer"
        :class="{
          opened:
            showBar,
          floating: fullScreenModeState && isScreenShare,
        }"
      ></div>
    </div>
    <div
      class="right-block-spacer"
      :class="{
        opened: showRightBlock,
        floating: fullScreenModeState && isScreenShare,
      }"
    ></div>
  </div>
</template>
<style scoped>
.active-room-container {
  width: 100vw;
  height: 100dvh;
  display: flex;
  background-color: #1e1818;
  background-image: var(--bg-image);
  background-repeat: repeat;
  position: relative;
  overflow: hidden;
}

.active-room-container .left-block {
  height: 100%;
  width: 100px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

.active-room-container .left-block .main-content {
  width: 100%;
  height: 100px;
  flex-grow: 1;
  user-select: none;
}

.active-room-container .left-block .bottom-block-spacer {
  width: 100%;
  height: 0px;
  transition: height 0.3s ease-in-out;
}

.active-room-container .left-block .bottom-block-spacer.opened:not(.floating) {
  height: 76px;
}
.active-room-container .left-block .bottom-block-spacer.floating {
  height: 0px;
}

.active-room-container .left-block .bottom-block {
  height: 76px;
  padding: 8px;
  transition: transform 0.3s ease-in-out;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(76px);
}

.active-room-container .left-block .bottom-block.opened {
  transform: translateY(0px);
}

.active-room-container .right-block-spacer {
  transition: width 0.3s ease-in-out;
  height: 100%;
}

.active-room-container .right-block {
  transition: transform 0.3s ease-in-out;
  padding: 8px 8px 8px 0;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0px;
  display: flex;
  flex-direction: column;
}

@media screen and (min-width: 811px) {
  .active-room-container .right-block-spacer {
    width: 0px;
  }
  .active-room-container .right-block-spacer.opened:not(.floating) {
    width: 367px;
  }
  .active-room-container .right-block {
    width: 367px;
    transform: translateX(367px);
  }
  .active-room-container .right-block.opened {
    transform: translateX(0px);
  }
}

@media screen and (max-width: 810px) {
  .active-room-container .right-block-spacer {
    width: 0px;
    height: 100%;
  }
  .active-room-container .right-block {
    position: absolute;
    top: 12px;
    left: 12px;
    right: 12px;
    bottom: 68px;
    z-index: 30;
    transform: translateY(100%);
    z-index: 30;
  }

  .active-room-container .right-block.opened {
    transform: translateY(0px);
  }
}

.spacer {
  width: 356px;
  height: 0px;
  transition: height 0.3s ease-in-out;
  flex-shrink: 0;
  pointer-events: none;
}

.spacer.opened {
  height: 208.25px;
}

.speaker-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  pointer-events: none;
  z-index: 20;
}

.speaker-container > * {
  pointer-events: auto;
}

.speaker-container .left-block {
  pointer-events: none;
}

.speaker-container .right-block {
  pointer-events: none;
}

.speaker-container .main-content * {
  pointer-events: auto;
}

.speaker-container .bottom-block * {
  pointer-events: auto;
}

.speaker-container .right-block * {
  pointer-events: auto;
}

.bottom-block-spacer {
  pointer-events: none !important;
}

.right-block-spacer {
  pointer-events: none !important;
}
</style>
